import React, { useState } from "react";
import "../css/switchMedia.css";
import { GridIcon, ListDisplayIcon } from "../../../../../asset-controller-mod/asset-serv/icons/iconExporter";


// ─── Types ────────────────────────────────────────────────────────────────────

export type SwitchMediaView = "grid" | "list";

export interface SwitchMediaProps {
  value: SwitchMediaView;
  onChange: (value: SwitchMediaView) => void;
  disabled?: boolean;
  className?: string;
}

type BtnState = "default" | "hover" | "focus" | "selected";

// ─── Single Button ────────────────────────────────────────────────────────────

interface SwitchBtnProps {
  side: "left" | "right";
  isSelected: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}

const SwitchBtn: React.FC<SwitchBtnProps> = ({
  side,
  isSelected,
  disabled,
  ariaLabel,
  onClick,
  children,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  let state: BtnState = "default";
  if (isSelected) state = "selected";
  else if (focused) state = "focus";
  else if (hovered) state = "hover";

  return (
    <button
      type="button"
      className={[
        "scl--cms-components-switchMedia-btn",
        `scl--cms-components-switchMedia-btn--${side}`,
        `scl--cms-components-switchMedia-btn--${state}`,
      ].join(" ")}
      onClick={onClick}
      onMouseEnter={() => !isSelected && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => !isSelected && setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
    >
      {children}
    </button>
  );
};

// ─── SwitchMedia Component ────────────────────────────────────────────────────

const SwitchMedia: React.FC<SwitchMediaProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
}) => {
  return ( 
    <div //NOSONAR
      className={`scl--cms-components-switchMedia${disabled ? " scl--cms-components-switchMedia--disabled" : ""} ${className}`.trim()}
      role="group"
      aria-label="View mode"
    >
      <SwitchBtn
        side="left"
        isSelected={value === "grid"}
        disabled={disabled}
        ariaLabel="Grid view"
        onClick={() => !disabled && onChange("grid")}
      >
        <GridIcon />
      </SwitchBtn>

      <SwitchBtn
        side="right"
        isSelected={value === "list"}
        disabled={disabled}
        ariaLabel="List view"
        onClick={() => !disabled && onChange("list")}
      >
        <ListDisplayIcon />
      </SwitchBtn>
    </div>
  );
};

export default SwitchMedia;